import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { getAllTestimonials, updateTestimonialStatus, deleteTestimonial } from '../../services/firebase/testimonialService';
import { getAllNotificationCounts } from '../../services/firebase/notificationService';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch all testimonials on component mount
  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const data = await getAllTestimonials();
      setTestimonials(data);
    } catch (err) {
      setError('Failed to load testimonials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle approval/rejection of testimonials
  const handleStatusChange = async (id, approved) => {
    try {
      await updateTestimonialStatus(id, approved);
      setTestimonials(testimonials.map(testimonial => 
        testimonial.id === id ? { ...testimonial, approved } : testimonial
      ));
      
      // Refresh notification counts
      try {
        await getAllNotificationCounts();
      } catch (error) {
        console.error('Error refreshing notification counts:', error);
      }
      
      setSuccess(`Testimonial ${approved ? 'approved' : 'rejected'} successfully.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update testimonial status. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Handle testimonial deletion
  const handleDelete = (testimonial) => {
    setCurrentTestimonial(testimonial);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteTestimonial(currentTestimonial.id);
      setTestimonials(testimonials.filter(t => t.id !== currentTestimonial.id));
      
      // Refresh notification counts
      try {
        await getAllNotificationCounts();
      } catch (error) {
        console.error('Error refreshing notification counts:', error);
      }
      
      setSuccess('Testimonial deleted successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete testimonial. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setShowDeleteModal(false);
      setCurrentTestimonial(null);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <Container className="py-5">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-4">Manage Testimonials</h2>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : testimonials.length === 0 ? (
                <Alert variant="info">No testimonials have been submitted yet.</Alert>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Author</th>
                      <th>Rating</th>
                      <th>Content</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testimonials.map((testimonial) => (
                      <tr key={testimonial.id}>
                        <td>{formatDate(testimonial.createdAt)}</td>
                        <td>
                          <strong>{testimonial.author}</strong>
                          <br />
                          <small className="text-muted">{testimonial.title}</small>
                        </td>
                        <td>
                          <span style={{ color: '#e0c068', whiteSpace: 'nowrap' }}>
                            {testimonial.rating
                              ? '★'.repeat(testimonial.rating) + '☆'.repeat(5 - testimonial.rating)
                              : '—'}
                          </span>
                        </td>
                        <td>
                          <div style={{ maxHeight: '100px', overflow: 'auto' }}>
                            {testimonial.content}
                          </div>
                        </td>
                        <td>
                          {testimonial.approved ? (
                            <Badge bg="success">Approved</Badge>
                          ) : (
                            <Badge bg="warning" text="dark">Pending</Badge>
                          )}
                        </td>
                        <td>
                          {!testimonial.approved && (
                            <Button 
                              variant="outline-success" 
                              size="sm" 
                              className="me-2"
                              onClick={() => handleStatusChange(testimonial.id, true)}
                            >
                              <FontAwesomeIcon icon={faCheck} /> Approve
                            </Button>
                          )}
                          {testimonial.approved && (
                            <Button 
                              variant="outline-warning" 
                              size="sm" 
                              className="me-2"
                              onClick={() => handleStatusChange(testimonial.id, false)}
                            >
                              <FontAwesomeIcon icon={faTimes} /> Unapprove
                            </Button>
                          )}
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDelete(testimonial)}
                          >
                            <FontAwesomeIcon icon={faTrash} /> Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this testimonial from {currentTestimonial?.author}?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminTestimonials;